/**
 * ReportPrintPreview — Financial-report print preview modal.
 *
 * Uses the same portal + @media-print pattern as MasterReportPreview so that
 * only the white "paper" area prints, not the surrounding application chrome.
 *
 * Usage:
 *   <ReportPrintPreview isOpen={showPrint} onClose={() => setShowPrint(false)}
 *                       title="Trial Balance" orientation="landscape">
 *     <ReportHeader reportTitle="..." ... />
 *     <table>...</table>
 *     <ReportFooter />
 *   </ReportPrintPreview>
 */
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X } from 'lucide-react';

interface ReportPrintPreviewProps {
  isOpen:       boolean;
  onClose:      () => void;
  title:        string;
  orientation?: 'landscape' | 'portrait';
  children:     ReactNode;
}

export const ReportPrintPreview = ({
  isOpen,
  onClose,
  title,
  orientation = 'landscape',
  children,
}: ReportPrintPreviewProps) => {

  useEffect(() => {
    if (!isOpen) return;

    const style = document.createElement('style');
    style.setAttribute('data-rpp', '');
    style.textContent = `
      @media print {
        body > *:not([data-rpp-root]) { display: none !important; }
        [data-rpp-root] {
          position: fixed !important; inset: 0 !important;
          background: white !important; z-index: 9999 !important;
          overflow: visible !important;
        }
        [data-rpp-no-print] { display: none !important; }
        @page { margin: 10mm 14mm; size: A4 ${orientation}; }
        thead { display: table-header-group; }
        tr    { page-break-inside: avoid; }
      }
    `;
    document.head.appendChild(style);

    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);

    return () => {
      document.querySelector('[data-rpp]')?.remove();
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose, orientation]);

  if (!isOpen) return null;

  const maxW = orientation === 'landscape' ? '1140px' : '860px';

  return createPortal(
    <div data-rpp-root="" style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>

      {/* Dark overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(4px)',
      }} />

      {/* Modal panel */}
      <div style={{
        position: 'relative', display: 'flex', flexDirection: 'column',
        height: '100%', maxWidth: maxW, margin: '0 auto',
      }}>

        {/* ── Toolbar (hidden when printing) ─────────────────────────────── */}
        <div
          data-rpp-no-print=""
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 16px', background: '#1e3a5f', color: 'white', flexShrink: 0,
          }}
        >
          <span style={{ fontSize: '13px', fontWeight: 600 }}>
            {title} — Print Preview
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => window.print()}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px', borderRadius: '6px',
                background: '#16a34a', color: 'white',
                border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: 600,
              }}
            >
              <Printer size={14} />
              Print
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center',
                padding: '6px', borderRadius: '6px',
                background: 'rgba(255,255,255,0.15)', color: 'white',
                border: 'none', cursor: 'pointer',
              }}
              title="Close preview (Esc)"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Scrollable paper canvas ─────────────────────────────────────── */}
        <div style={{
          flex: 1, overflowY: 'auto',
          padding: '24px 16px', background: '#e5e7eb',
        }}>
          {/* White "paper" */}
          <div
            id="rpp-paper"
            style={{
              background: 'white',
              padding: '28px 36px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
              minHeight: '180mm',
              fontFamily: 'Arial, Helvetica, sans-serif',
              fontSize: '10px',
              color: '#111827',
              lineHeight: 1.5,
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
